import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TrimWhitespaceDirective } from '../../../core/directives/trim.directive';
import { PhoneFormatDirective } from '../../../core/directives/phone-format.directive';
import { trimFormGroup } from '../../../core/utils/form.utils';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { S3ImageUploadService } from '../../../core/services/s3-image-upload.service';
import { CreateFamilyMemberRequest, CreateFamilySpaceAndMemberRequest } from '../../../core/models/auth.models';

interface FamilySpaceDraft {
  familyName: string;
  welcomeMessage: string;
  displayPictureName: string;
}

interface FamilyMemberDraft extends CreateFamilyMemberRequest {
  relationshipLabel: string;
  photoPreview: string | null;
}

@Component({
  selector: 'app-create-family-space',
  templateUrl: './create-family-space.html',
  styleUrl: './create-family-space.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, TrimWhitespaceDirective, PhoneFormatDirective],
})
export class CreateFamilySpace implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private s3 = inject(S3ImageUploadService);

  /** Internal step 1-3; step 1 = "Step 1 of 2" (50%), steps 2-3 = "Step 2 of 2" (100%) */
  readonly currentStep = signal<number>(1);
  readonly isLoading = signal(false);
  readonly isLogoUploading = signal(false);
  readonly isMemberPhotoUploading = signal(false);
  readonly isEmailChecking = signal(false);
  readonly memberEmailError = signal('');
  readonly familyDraft = signal<FamilySpaceDraft | null>(null);
  readonly addedMembers = signal<FamilyMemberDraft[]>([]);

  /** Tracks which step the user came from when entering step 3 (loved-one form).
   *  1 = came directly from family space form (step 1) or via ?step=loved-one redirect
   *  2 = came from cards overview (step 2) via "Add Another Loved One"
   */
  private step3PreviousStep: 1 | 2 = 1;

  /** S3 keys stored after upload � sent in API payload */
  private logoS3Key = '';
  private memberPhotoS3Key = '';

  readonly relationshipOptions = [
    { id: 1, label: 'Parent' },
    { id: 2, label: 'Sibling' },
    { id: 3, label: 'Child' },
    { id: 4, label: 'Spouse' },
    { id: 5, label: 'Friend' },
    { id: 6, label: 'Other' },
  ] as const;

  ngOnInit(): void {
    // If isFamilySpaceAdded=true but isFamilyMemberAdded=false, jump straight to loved-one step
    const step = this.route.snapshot.queryParamMap.get('step');
    if (step === 'loved-one') {
      this.step3PreviousStep = 1;
      this.memberForm.markAsUntouched();
      this.memberForm.markAsPristine();
      this.currentStep.set(3);
    }
  }

  readonly step1Form = this.fb.group({
    familyName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    welcomeMessage: [''],
    familyLogo: [null as File | null],
  });

  readonly memberForm = this.fb.group({
    firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    relationshipTypeId: [null as number | null, Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    photo: [null as File | null],
  });

  readonly logoPreview = signal<string | null>(null);
  readonly logoMeta = signal<string>('');
  readonly optionalPhotoPreview = signal<string | null>(null);
  readonly optionalPhotoMeta = signal<string>('');
  readonly isLogoDragOver = signal(false);
  readonly isMemberPhotoDragOver = signal(false);

  get displayStep(): number { return this.currentStep() === 1 ? 1 : 2; }
  get progressPercent(): number { return this.currentStep() === 1 ? 50 : 100; }
  get s1(): typeof this.step1Form.controls { return this.step1Form.controls; }
  get mf(): typeof this.memberForm.controls { return this.memberForm.controls; }

  private isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  onLogoDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isLogoDragOver.set(true);
  }

  onLogoDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isLogoDragOver.set(false);
  }

  onLogoDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isLogoDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!this.isImageFile(file)) {
      this.toastService.error('Only image files are allowed (JPG, PNG, GIF, WebP, etc.).');
      return;
    }
    this.processLogoFile(file);
  }

  onLogoChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!this.isImageFile(file)) {
      this.toastService.error('Only image files are allowed.');
      (event.target as HTMLInputElement).value = '';
      return;
    }
    this.processLogoFile(file);
  }

  private processLogoFile(file: File): void {
    this.step1Form.patchValue({ familyLogo: file });
    this.logoMeta.set(file.name);
    const reader = new FileReader();
    reader.onload = e => { this.logoPreview.set(e.target?.result as string); this.cdr.markForCheck(); };
    reader.readAsDataURL(file);

    this.isLogoUploading.set(true);
    this.s3.upload(file, 'familyLogo').then(result => {
      this.logoS3Key = result.fileName;
      this.isLogoUploading.set(false);
      this.cdr.markForCheck();
    }).catch(() => {
      this.toastService.error('Failed to upload family logo. Please try again.');
      this.isLogoUploading.set(false);
      this.cdr.markForCheck();
    });
  }

  removeLogo(): void {
    this.logoPreview.set(null);
    this.logoMeta.set('');
    this.logoS3Key = '';
    this.step1Form.patchValue({ familyLogo: null });
  }

  onMemberPhotoDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isMemberPhotoDragOver.set(true);
  }

  onMemberPhotoDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isMemberPhotoDragOver.set(false);
  }

  onMemberPhotoDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isMemberPhotoDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!this.isImageFile(file)) {
      this.toastService.error('Only image files are allowed (JPG, PNG, GIF, WebP, etc.).');
      return;
    }
    this.processMemberPhotoFile(file);
  }

  onOptionalPhotoChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!this.isImageFile(file)) {
      this.toastService.error('Only image files are allowed.');
      (event.target as HTMLInputElement).value = '';
      return;
    }
    this.processMemberPhotoFile(file);
  }

  private processMemberPhotoFile(file: File): void {
    this.memberForm.patchValue({ photo: file });
    this.optionalPhotoMeta.set(file.name);
    const reader = new FileReader();
    reader.onload = e => { this.optionalPhotoPreview.set(e.target?.result as string); this.cdr.markForCheck(); };
    reader.readAsDataURL(file);

    this.isMemberPhotoUploading.set(true);
    this.s3.upload(file, 'memberPhoto').then(result => {
      this.memberPhotoS3Key = result.fileName;
      this.isMemberPhotoUploading.set(false);
      this.cdr.markForCheck();
    }).catch(() => {
      this.toastService.error('Failed to upload member photo. Please try again.');
      this.isMemberPhotoUploading.set(false);
      this.cdr.markForCheck();
    });
  }

  removeMemberPhoto(): void {
    this.optionalPhotoPreview.set(null);
    this.optionalPhotoMeta.set('');
    this.memberPhotoS3Key = '';
    this.memberForm.patchValue({ photo: null });
  }

  onFamilyContinue(): void {
    trimFormGroup(this.step1Form);
    if (this.step1Form.invalid) {
      this.step1Form.markAllAsTouched();
      return;
    }

    this.familyDraft.set({
      familyName: this.step1Form.controls.familyName.value?.trim() ?? '',
      welcomeMessage: this.step1Form.controls.welcomeMessage.value?.trim() ?? '',
      displayPictureName: this.logoS3Key,
    });

    // If loved ones already added go to listing view, otherwise open the form directly
    if (this.addedMembers().length > 0) {
      this.currentStep.set(2);
    } else {
      this.step3PreviousStep = 1; // came straight from family space form
      this.memberForm.markAsUntouched();
      this.memberForm.markAsPristine();
      this.currentStep.set(3);
    }
  }

  /** Tracks the in-flight email validation promise so onAddToFamily can await it */
  private emailCheckPromise: Promise<void> | null = null;

  async onMemberEmailBlur(): Promise<void> {
    const email = this.memberForm.controls.email.value?.trim() ?? '';
    if (!email) {
      this.clearEmailExistsError();
      return;
    }

    if (this.memberForm.controls.email.invalid) return;

    this.isEmailChecking.set(true);
    this.emailCheckPromise = this.authService.validateFamilyMemberEmail(email).then(resp => {
      if (!resp.status) {
        const existingErrors = this.memberForm.controls.email.errors ?? {};
        this.memberForm.controls.email.setErrors({ ...existingErrors, emailExists: true });
        this.memberEmailError.set(resp.message);
      } else {
        this.clearEmailExistsError();
      }
      this.isEmailChecking.set(false);
      this.emailCheckPromise = null;
      this.cdr.markForCheck();
    });

    await this.emailCheckPromise;
  }

  clearEmailExistsError(): void {
    const currentErrors = this.memberForm.controls.email.errors;
    if (!currentErrors) {
      this.memberEmailError.set('');
      return;
    }

    if ('emailExists' in currentErrors) {
      const { emailExists, ...rest } = currentErrors;
      this.memberForm.controls.email.setErrors(Object.keys(rest).length ? rest : null);
    }

    this.memberEmailError.set('');
  }

  async onAddToFamily(): Promise<void> {
    // If the email blur validation is still in-flight (race: blur + click at same time), wait for it
    if (this.emailCheckPromise) {
      await this.emailCheckPromise;
    }

    trimFormGroup(this.memberForm);
    if (this.memberForm.invalid) {
      this.memberForm.markAllAsTouched();
      return;
    }

    if (this.memberForm.controls.email.errors?.['emailExists']) {
      this.memberForm.controls.email.markAsTouched();
      return;
    }

    const relationId = Number(this.memberForm.controls.relationshipTypeId.value ?? 0);
    const relation = this.relationshipOptions.find(r => r.id === relationId);

    const member: FamilyMemberDraft = {
      firstName: this.memberForm.controls.firstName.value?.trim() ?? '',
      lastName: this.memberForm.controls.lastName.value?.trim() ?? '',
      email: this.memberForm.controls.email.value?.trim() ?? '',
      phoneNumber: (this.memberForm.controls.phone.value ?? '').replace(/\D/g, ''),
      profilePhotoFileName: this.memberPhotoS3Key,
      relationshipTypeId: relationId,
      relationshipLabel: relation?.label ?? 'Other',
      photoPreview: this.optionalPhotoPreview(),
    };

    this.addedMembers.update(prev => [...prev, member]);
    this.resetMemberForm();
    this.currentStep.set(2); // return to cards overview
    this.cdr.markForCheck();
  }

  async onSkipForNowFromMember(): Promise<void> {
    this.resetMemberForm();
    this.isLoading.set(true);
    await this.authService.skipFamilySpace();
    this.isLoading.set(false);
    this.router.navigate(['/app/stories'], { replaceUrl: true });
  }

  onAddAnotherLovedOne(): void {
    this.step3PreviousStep = 2; // came from cards overview
    this.memberForm.markAsUntouched();
    this.memberForm.markAsPristine();
    this.currentStep.set(3);
  }

  removeMember(index: number): void {
    this.addedMembers.update(prev => prev.filter((_, i) => i !== index));
    this.cdr.markForCheck();
  }

  async onFinalSubmit(): Promise<void> {
    const family = this.familyDraft();
    if (!family) {
      this.toastService.error('Please complete family space details first.');
      this.currentStep.set(1);
      return;
    }

    const payload: CreateFamilySpaceAndMemberRequest = {
      familyName: family.familyName,
      displayPictureName: family.displayPictureName,
      welcomeMessage: family.welcomeMessage,
      members: this.addedMembers().map(member => ({
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
        phoneNumber: member.phoneNumber,
        profilePhotoFileName: member.profilePhotoFileName,
        relationshipTypeId: member.relationshipTypeId,
      })),
    };

    this.isLoading.set(true);
    const resp = await this.authService.createFamilySpaceAndMember(payload);
    this.isLoading.set(false);

    if (resp.status) {
      this.toastService.success(resp.message);
      this.router.navigate(['/app/stories'], { replaceUrl: true });
    } else {
      this.toastService.error(resp.message);
    }
  }

  async skipToDashboard(): Promise<void> {
    this.isLoading.set(true);
    await this.authService.skipFamilySpace();
    this.isLoading.set(false);
    this.router.navigate(['/app/stories'], { replaceUrl: true });
  }

  goNext(): void {
    if (this.currentStep() === 1) {
      this.onFamilyContinue();
    } else if (this.currentStep() === 2) {
      this.currentStep.set(2);
    }
  }

  goBack(): void {
    if (this.currentStep() === 3) {
      // Go back to wherever step 3 was entered from
      this.currentStep.set(this.step3PreviousStep);
      return;
    }
    if (this.currentStep() === 2) {
      this.currentStep.set(1); // cards overview → family setup
    }
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }

  private resetMemberForm(): void {
    this.memberForm.reset({
      firstName: '',
      lastName: '',
      relationshipTypeId: null,
      email: '',
      phone: '',
      photo: null,
    });
    this.optionalPhotoMeta.set('');
    this.optionalPhotoPreview.set(null);
    this.memberEmailError.set('');
    this.memberPhotoS3Key = '';
  }
}
